import type {
  Midi,
  MidiChannel,
  MidiControlChange,
  MidiInstrument,
  MidiMetaEvent,
  MidiNote,
  MidiPitchBend,
  MidiProgramChange,
  MidiSysEx,
  MidiTrack,
  MidiUnknownMeta,
} from "@midi-fantom-utils/midi-core";
import { DRUM_CHANNEL } from "@midi-fantom-utils/midi-core";
import { getInstrumentMeta } from "@midi-fantom-utils/midi-core";
import { buildFantomTracks } from "./fantom.js";
import { createEmptyTrack, cloneInstrument } from "./shared.js";
import { isDrumByChannel } from "./drum.js";
import { recomputeDurationSeconds } from "./duration.js";

/**
 * Раскладывает исходный `Midi` в multi-track представление: если хотя бы
 * один трек содержит ноты/CC/pitch bend/programChanges/meta/unknownMeta,
 * разнесённые по разным каналам (что всегда верно для format-0 — один
 * MTrk на 16 каналов), каждый канал получает свой `MidiTrack`. Это критично
 * для format-0 файлов, где парсер кладёт все события в один MTrk с одним
 * каналом — без раскладки `buildFantomTracks` видит только drum/non-drum по
 * `track.channel === 9` и для большинства каналов ошибочно классифицирует.
 *
 * Реализация опирается на поле `channel` в `MidiNote` /
 * `MidiControlChange` / `MidiPitchBend` / `MidiProgramChange`, выставленное
 * парсером. Если поле отсутствует (например, при ручном конструировании в
 * тестах) — событие атрибутируется к `track.channel` исходного трека, что
 * соответствует поведению format-1/2 источников.
 *
 * Функция — no-op для исходников, у которых все треки уже single-channel
 * (формат 1/2 без пересечения каналов между треками): такие MTrk
 * остаются как есть и раскладка не запускается.
 */
export const toMultiTrackFormat = (midi: Midi): Midi => {
  const out: MidiTrack[] = [];

  for (const track of midi.tracks) {
    if (
      track.notes.length === 0 &&
      track.pitchBends.length === 0 &&
      track.controlChanges.size === 0 &&
      track.programChanges.length === 0 &&
      track.meta.length === 0 &&
      track.unknownMeta.length === 0 &&
      track.sysex.length === 0
    ) {
      // Совсем пустой трек (нет ни notes, ни PC, ни CC, ни pitch bend, ни
      // meta, ни sysEx) — оставляем как есть. Раньше здесь была ветка,
      // заменявшая «пустой header track с meta/именем» на
      // `createEmptyTrack(channel)`, но она была недостижима: чтобы
      // попасть в этот блок, `track.meta.length` уже должен быть 0.
      out.push(track);
      continue;
    }
    if (!trackSpansMultipleChannels(track)) {
      // Трек уже single-channel — оставляем его без изменений, чтобы не
      // мерджить несколько MTrk format-1/2, которые случайно используют
      // один канал.
      out.push(track);
      continue;
    }

    // Multi-channel трек → раскладываем по каналам.
    const perChannel = new Map<MidiChannel, TrackDraft>();

    for (const note of track.notes) {
      const ch: MidiChannel = note.channel ?? track.channel;
      const draft = getOrCreate(perChannel, ch, track.channel, track.instrument);
      pushNote(draft, note, ch);
    }
    for (const [number, list] of track.controlChanges) {
      for (const cc of list) {
        const ch: MidiChannel = cc.channel ?? track.channel;
        const draft = getOrCreate(perChannel, ch, track.channel, track.instrument);
        pushCc(draft, cc, number, ch);
      }
    }
    for (const pc of track.programChanges) {
      const ch: MidiChannel = pc.channel ?? track.channel;
      const draft = getOrCreate(perChannel, ch, track.channel, track.instrument);
      pushPc(draft, pc, ch);
    }
    for (const pb of track.pitchBends) {
      const ch: MidiChannel = pb.channel ?? track.channel;
      const draft = getOrCreate(perChannel, ch, track.channel, track.instrument);
      pushPb(draft, pb, ch);
    }
    for (const m of track.meta) {
      // Текстовые meta не имеют канала — оставляем на fallback-канале трека.
      const ch: MidiChannel = track.channel;
      const draft = getOrCreate(perChannel, ch, track.channel, track.instrument);
      pushMeta(draft, m);
    }
    for (const um of track.unknownMeta) {
      // unknownMeta не имеет канала — оставляем на fallback-канале трека.
      const ch: MidiChannel = track.channel;
      const draft = getOrCreate(perChannel, ch, track.channel, track.instrument);
      pushUnknownMeta(draft, um);
    }
    for (const sx of track.sysex) {
      // SysEx не имеет канала — оставляем на fallback-канале трека.
      const ch: MidiChannel = track.channel;
      const draft = getOrCreate(perChannel, ch, track.channel, track.instrument);
      pushSysex(draft, sx);
    }

    const sorted = [...perChannel.keys()].sort((a, b) => a - b);
    for (const ch of sorted) {
      out.push(freezeTrack(perChannel.get(ch)!));
    }
  }

  if (out.length === midi.tracks.length) {
    let sameRefs = true;
    for (let i = 0; i < out.length; i += 1) {
      if (out[i] !== midi.tracks[i]) {
        sameRefs = false;
        break;
      }
    }
    if (sameRefs) {
      // Ничего не изменилось — отдаём исходник без пересчёта длительности.
      return midi;
    }
  }

  return recomputeDurationSeconds({
    ...midi,
    format: 1,
    tracks: out,
  });
};

const trackSpansMultipleChannels = (track: MidiTrack): boolean => {
  const fallback = track.channel;
  let firstChannel: MidiChannel | null = null;
  for (const note of track.notes) {
    const ch: MidiChannel = note.channel ?? fallback;
    if (firstChannel === null) {
      firstChannel = ch;
    } else if (firstChannel !== ch) {
      return true;
    }
  }
  if (firstChannel !== null && firstChannel !== fallback) return true;
  for (const list of track.controlChanges.values()) {
    for (const cc of list) {
      const ch: MidiChannel = cc.channel ?? fallback;
      if (firstChannel === null) {
        firstChannel = ch;
      } else if (firstChannel !== ch) {
        return true;
      }
    }
  }
  for (const pc of track.programChanges) {
    const ch: MidiChannel = pc.channel ?? fallback;
    if (firstChannel === null) {
      firstChannel = ch;
    } else if (firstChannel !== ch) {
      return true;
    }
  }
  for (const pb of track.pitchBends) {
    const ch: MidiChannel = pb.channel ?? fallback;
    if (firstChannel === null) {
      firstChannel = ch;
    } else if (firstChannel !== ch) {
      return true;
    }
  }
  return false;
};

const getOrCreate = (
  map: Map<MidiChannel, TrackDraft>,
  channel: MidiChannel,
  fallbackChannel: MidiChannel,
  sourceInstrument: MidiInstrument
): TrackDraft => {
  let draft = map.get(channel);
  if (!draft) {
    draft = createDraft(channel, fallbackChannel, sourceInstrument);
    map.set(channel, draft);
  }
  return draft;
};

type TrackDraft = {
  track: MidiTrack;
  notes: MidiNote[];
  cc: Map<number, MidiControlChange[]>;
  pc: MidiProgramChange[];
  pb: MidiPitchBend[];
  sysex: MidiSysEx[];
  meta: MidiMetaEvent[];
  unknownMeta: MidiUnknownMeta[];
  hasExplicitInstrument: boolean;
};

const createDraft = (
  channel: MidiChannel,
  fallbackChannel: MidiChannel,
  sourceInstrument: MidiInstrument
): TrackDraft => {
  const base = createEmptyTrack(channel);
  // Стартовый инструмент:
  // - Канал 9 (drum) → «standard kit / percussive» (GM Level 1).
  // - Канал per-channel совпадает с исходным `track.channel` → клонируем
  //   инструмент источника как первичную оценку.
  // - Иначе → дефолтный piano.
  // В melodic-случаях первый же PC (`0xc0`) для этого канала переопределит
  // `instrument.number/name/family` (см. `pushPc`). Drum-канал PC
  // игнорирует, и итоговый «standard kit» остаётся как финальное имя.
  const instrument: MidiInstrument =
    channel === DRUM_CHANNEL
      ? { family: "percussive", number: 0, name: "standard kit", percussion: true }
      : channel === fallbackChannel
        ? cloneInstrument(sourceInstrument)
        : {
            family: "piano",
            number: 0,
            name: "acoustic grand piano",
            percussion: false,
          };
  return {
    track: { ...base, instrument, channel },
    notes: [],
    cc: new Map(),
    pc: [],
    pb: [],
    sysex: [],
    meta: [],
    unknownMeta: [],
    hasExplicitInstrument: false,
  };
};

const pushNote = (draft: TrackDraft, note: MidiNote, channel: MidiChannel): void => {
  draft.notes.push({ ...note, channel });
};

const pushCc = (
  draft: TrackDraft,
  cc: MidiControlChange,
  number: number,
  channel: MidiChannel
): void => {
  const list = draft.cc.get(number) ?? [];
  list.push({ ...cc, channel });
  draft.cc.set(number, list);
};

const pushPc = (draft: TrackDraft, pc: MidiProgramChange, channel: MidiChannel): void => {
  // Первый PC (`0xc0`) для канала переопределяет `draft.track.instrument`
  // (берёт `number/name/family` из таблицы GM Level 1). Это нужно для
  // format-0 файлов, где `track.instrument` источника относится к
  // «первому попавшемуся» каналу и не описывает остальные. На ударном
  // канале PC игнорируется: `draft.track.instrument` остаётся «standard kit»
  // (см. `createDraft`), `freezeTrack` подтверждает `percussion: true`.
  if (!draft.hasExplicitInstrument && channel !== DRUM_CHANNEL) {
    const meta = getInstrumentMeta(pc.value, channel);
    draft.track = {
      ...draft.track,
      instrument: {
        number: pc.value,
        family: meta.family,
        name: meta.name,
        percussion: meta.percussion,
      },
    };
    draft.hasExplicitInstrument = true;
  }
  draft.pc.push({ ...pc, channel });
};

const pushPb = (draft: TrackDraft, pb: MidiPitchBend, channel: MidiChannel): void => {
  draft.pb.push({ ...pb, channel });
};

const pushMeta = (draft: TrackDraft, m: MidiMetaEvent): void => {
  draft.meta.push(m);
};

const pushUnknownMeta = (draft: TrackDraft, um: MidiUnknownMeta): void => {
  draft.unknownMeta.push(um);
};

const pushSysex = (draft: TrackDraft, sx: MidiSysEx): void => {
  draft.sysex.push(sx);
};

const freezeTrack = (draft: TrackDraft): MidiTrack => {
  const cc = new Map<number, ReadonlyArray<MidiControlChange>>();
  for (const [number, list] of draft.cc) {
    cc.set(number, list.slice().sort((a, b) => a.ticks - b.ticks));
  }
  // Наследуем percussion-флаг от канала: канал 9 — всегда ударные.
  const isPercussion = isDrumByChannel({
    ...draft.track,
    notes: draft.notes,
  });
  return {
    ...draft.track,
    instrument: isPercussion
      ? { ...draft.track.instrument, percussion: true }
      : draft.track.instrument,
    notes: draft.notes
      .slice()
      .sort((a, b) => a.ticks - b.ticks || a.midi - b.midi),
    controlChanges: cc,
    programChanges: draft.pc.slice().sort((a, b) => a.ticks - b.ticks),
    pitchBends: draft.pb.slice().sort((a, b) => a.ticks - b.ticks),
    sysex: draft.sysex,
    meta: draft.meta.slice().sort((a, b) => a.ticks - b.ticks),
    unknownMeta: draft.unknownMeta.slice().sort((a, b) => a.ticks - b.ticks),
  };
};

/**
 * Бинарный шаг трансформации: переразлагает исходный `Midi` по каналам
 * для Roland Fantom (см. `buildFantomTracks`). Перед переразложением
 * прогоняет `toMultiTrackFormat`, чтобы format-0 источники корректно
 * разнеслись по 16 каналам.
 *
 * Возвращает `format: 1`, 16 трэков (пустые — на месте), очищенный
 * `header.meta` (имена и текстовые meta) — финальный encoder их не
 * использует, а `Report` от пайплайна не должен показывать артефакты
 * исходника. Имена треков очищаются внутри `buildFantomTracks`.
 */
export const packMidi = (midi: Midi): Midi => {
  const expanded = toMultiTrackFormat(midi);

  const headerWithoutMeta: Midi["header"] = {
    ...expanded.header,
    meta: [],
  };

  const drumIndices = new Set<number>();
  for (let i = 0; i < expanded.tracks.length; i++) {
    if (expanded.tracks[i]!.instrument.percussion) {
      drumIndices.add(i);
    }
  }
  const tracks = buildFantomTracks(expanded.tracks, drumIndices);

  return recomputeDurationSeconds({
    format: 1,
    header: headerWithoutMeta,
    tracks,
  });
};
